import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { useIntl } from "react-intl";
import type { IntlShape } from "react-intl";
import { Button } from "@/components/design/Button";
import { AdminMasterDetail } from "./AdminMasterDetail";
import { MenuItem } from "@/components/design/MenuItem";
import { NumberStepper } from "@/components/design/NumberStepper";
import { PlusButton } from "@/components/design/PlusButton";
import { Select } from "@/components/design/Select";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import {
  AssignQuestionnaireToGroupDocument,
  DeleteQuestionnaireDocument,
  QuestionnaireDetailDocument,
  QuestionnaireGroupsAdminDocument,
  QuestionnairesListDocument,
  SaveQuestionnaireDocument,
} from "@/graphql/generated/operations";
// codegen emits these as nominal `enum`s for the mutation input; the draft keeps
// plain strings (the enums' runtime values) and casts only at the save boundary.
import type { CreativityElement, QuestionnaireStatus } from "@/graphql/generated/operations";
import type { FieldOption } from "@/lib/field-util";
import { ELEMENT_CODES } from "@/lib/elements";
import { POWER_BY_ABBR } from "@/lib/power-library";
import { logApplicationFailure, userErrorMessage } from "@/lib/user-error";
import { QuestionnaireGroupEditor } from "./QuestionnaireGroupEditor";
import { StatusDot } from "./StatusDot";
import { MAX_POSITION, MAX_WEIGHT, buildGroupedList, groupName, groupVisibility } from "./questionnaire-groups";
import type { GroupRow, QuestionnaireRow } from "./questionnaire-groups";

type QStatus = "ACTIVE" | "INACTIVE";

// Admin authoring for grow questionnaires: list on the left, editor on the
// right. saveQuestionnaire upserts the whole graph (questionnaire
// + questions + weighted answer choices); null ids create. Embedded in the
// Systemeinstellungen → Fragebögen section (gated by write:any:questionnaire);
// genesis enforces the real permission server-side.
//
// Since genesis#183 the list has two levels: questionnaire groups (one group =
// one entry in the Grow play catalogue) and their members. The same detail
// column serves both, switched by the selection. Group membership is not part
// of saveQuestionnaire; it only moves through assignQuestionnaireToGroup, which
// is why the assignment block below commits on its own.

const inputCls =
  "w-full rounded-md bg-page px-3 py-2 text-smycles-meta leading-relaxed outline-none ring-1 ring-ink/10 transition focus:ring-ink-accent-ui";

// element enum → localized power name, for the element selects.
function useElOptions(): FieldOption[] {
  const intl = useIntl();
  return Object.entries(ELEMENT_CODES)
    .map(([el, abbr]) => {
      const nameId = POWER_BY_ABBR[abbr]?.nameId;
      return { value: el, label: nameId ? intl.formatMessage({ id: nameId }) : el };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

const STATUS_OPTION_IDS: { value: QStatus; labelId: string }[] = [
  { value: "ACTIVE", labelId: "admin.statusActive" },
  { value: "INACTIVE", labelId: "admin.statusInactive" },
];

type DraftChoice = { qnrAnswerChoiceId?: string; answer: string; weight: number };
type DraftQuestion = {
  qnrQuestionId?: string;
  question: string;
  creativityElement: string;
  status: string;
  choices: DraftChoice[];
};
type Draft = {
  questionnaireId?: string;
  name: string;
  instructions: string;
  creativityElement: string | null;
  status: QStatus;
  questions: DraftQuestion[];
};

/** null = nothing selected; a null id inside means "creating one of these". */
type Selection =
  | { kind: "questionnaire"; id: string | null }
  | { kind: "group"; id: string | null }
  | null;

function selectionKey(selection: NonNullable<Selection>): string {
  return `${selection.kind}:${selection.id ?? "new"}`;
}

function emptyQuestion(element: string): DraftQuestion {
  return {
    question: "",
    creativityElement: element || Object.keys(ELEMENT_CODES)[0] || "",
    status: "ACTIVE",
    choices: [
      { answer: "", weight: 0 },
      { answer: "", weight: 100 },
    ],
  };
}

export function QuestionnaireManager() {
  const intl = useIntl();
  const {
    data,
    error: listError,
    refetch,
  } = useQuery(QuestionnairesListDocument, {
    variables: { input: {} },
    fetchPolicy: "cache-and-network",
  });
  const {
    data: groupData,
    error: groupsError,
    refetch: refetchGroups,
  } = useQuery(QuestionnaireGroupsAdminDocument, {
    fetchPolicy: "cache-and-network",
  });
  // Either list failing makes every membership fact on this screen a guess: a
  // missing catalogue hides real members, a missing questionnaire list paints
  // live groups as empty. Both would then feed the delete dialog's "it holds no
  // questionnaires". So the counts, the catalogue notes and the per-group
  // actions stand down until both lists are back.
  const listsError = listError ?? groupsError ?? null;
  const list: QuestionnaireRow[] = data?.questionnaires ?? [];
  const groups: GroupRow[] = groupData?.questionnaireGroups ?? [];
  // Settled means "answered", success or failure: leaving it false on error would
  // drop every grouped questionnaire out of the list entirely.
  const catalogueSettled = groupData !== undefined || groupsError != null;
  const { sections, ungrouped } = buildGroupedList(groups, list, catalogueSettled);

  const [selected, setSelected] = useState<Selection>(null);
  // Lives here, not in the editors: a create flips the selection key (null id ->
  // real id) and remounts QuestionnaireGroupEditor/Editor, which would wipe a
  // locally-held "just saved" flag before the toast ever showed.
  const [justSaved, setJustSaved] = useState(false);
  useEffect(() => {
    if (!justSaved) return;
    const expiry = window.setTimeout(() => setJustSaved(false), 3000);
    return () => window.clearTimeout(expiry);
  }, [justSaved]);
  // Collapsed groups, by id. A group starts expanded; nothing here persists
  // across a reload, same as the list rail in AdminMasterDetail.
  const [collapsedGroupIds, setCollapsedGroupIds] = useState<Set<string>>(new Set());

  function flashSaved() {
    setJustSaved(true);
  }

  function toggleGroupCollapsed(groupId: string) {
    setCollapsedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }

  // A refetch that rejects is reported by its own hook's `error`, so it must not
  // become an unhandled rejection; the boolean says whether the lists are now
  // trustworthy.
  function refetchAll(): Promise<boolean> {
    return Promise.all([refetch(), refetchGroups()]).then(
      () => true,
      () => false,
    );
  }

  /**
   * Re-selecting only after the refetch has landed, and only if it landed.
   * Flipping the key first remounts the editor against a catalogue that does not
   * know the row yet, and it would come up as a blank create form over the name
   * the user just typed; doing it after a FAILED refetch would instead claim the
   * row does not exist seconds after creating it.
   */
  function selectAfterRefetch(next: NonNullable<Selection>) {
    void refetchAll().then((ok) => {
      if (ok) setSelected(next);
    });
  }

  const selectedSection =
    selected?.kind === "group" && selected.id
      ? (sections.find((s) => s.group.questionnaireGroupId === selected.id) ?? null)
      : null;
  const selectedQuestionnaire =
    selected?.kind === "questionnaire" && selected.id
      ? (list.find((q) => q.questionnaireId === selected.id) ?? null)
      : null;
  // An id the loaded list does not carry, which means somebody else deleted the
  // row: a selection is only ever made from a rendered list, and a save
  // re-selects only after its refetch has landed. Seeding a form from it would
  // show empty fields over real data and write them back on Save, so the editor
  // stays closed and says so instead.
  const selectionGone =
    (selected?.kind === "group" && selected.id !== null && selectedSection === null) ||
    (selected?.kind === "questionnaire" && selected.id !== null && selectedQuestionnaire === null);

  function questionnaireRow(q: QuestionnaireRow) {
    return (
      <li key={q.questionnaireId}>
        <MenuItem
          id={`admin-side-questionnaire-${q.questionnaireId}`}
          active={selected?.kind === "questionnaire" && selected.id === q.questionnaireId}
          onClick={() => setSelected({ kind: "questionnaire", id: q.questionnaireId })}
          trailing={<StatusDot status={String(q.status)} />}
        >
          {q.name}
        </MenuItem>
      </li>
    );
  }

  const notVisible = intl.formatMessage({
    id: "admin.notVisibleInGrow",
    defaultMessage: "Not in the Grow play catalogue.",
  });

  return (
    <>
    <AdminMasterDetail
      title={intl.formatMessage({ id: "admin.qTitle", defaultMessage: "Questionnaires" })}
      count={list.length}
      list={
        <>
          <div className="mb-3 flex flex-col gap-2">
            <Button
              id="admin-questionnaire-new"
              variant="primary"
              onClick={() => setSelected({ kind: "questionnaire", id: null })}
              className="w-full"
            >
              {intl.formatMessage({ id: "admin.newQuestionnaire", defaultMessage: "+ New questionnaire" })}
            </Button>
            <Button
              id="admin-group-new"
              variant="secondary"
              onClick={() => setSelected({ kind: "group", id: null })}
              className="w-full"
            >
              {intl.formatMessage({ id: "admin.newGroup", defaultMessage: "+ New group" })}
            </Button>
          </div>

          {listsError ? (
            <div className="mb-3 text-smycles-meta text-red-600">{userErrorMessage(intl, listsError)}</div>
          ) : null}

          {!listsError && sections.length === 0 && list.length === 0 ? (
            <div className="px-1 py-2 text-smycles-meta text-ink-muted">
              {intl.formatMessage({ id: "admin.noQuestionnaires", defaultMessage: "No questionnaires yet." })}
            </div>
          ) : null}

          <div className="space-y-3">
            {sections.map(({ group, members }) => {
              const visibility = groupVisibility(String(group.status), members, group.itemCount);
              const collapsed = collapsedGroupIds.has(group.questionnaireGroupId);
              const active = selected?.kind === "group" && selected.id === group.questionnaireGroupId;
              return (
                <div key={group.questionnaireGroupId} data-group-block={group.questionnaireGroupId}>
                  {/* The row itself owns the whole hover/active wash, as one
                      shared rounded surface — chevron and MenuItem are both
                      stripped of their own background/hover so nothing can
                      layer a second translucent tint on top and read darker
                      than the rest of the bar (:hover on a parent already
                      fires while the pointer is over either child). */}
                  <div
                    className={`flex items-center gap-0.5 rounded-md transition ${active ? "bg-ink/[0.06]" : "hover:bg-ink/[0.06]"}`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleGroupCollapsed(group.questionnaireGroupId)}
                      aria-expanded={!collapsed}
                      aria-label={intl.formatMessage(
                        {
                          id: "admin.toggleGroupCollapsed",
                          defaultMessage: "{state, select, collapsed {Expand} other {Collapse}} {name}",
                        },
                        { state: collapsed ? "collapsed" : "expanded", name: groupName(group.name) },
                      )}
                      className="shrink-0 rounded p-1.5 text-smycles-micro text-ink-muted transition hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-ui"
                    >
                      <span
                        className={`inline-block transition-transform duration-[var(--duration-base)] ${collapsed ? "" : "rotate-90"}`}
                      >
                        ›
                      </span>
                    </button>
                    <MenuItem
                      id={`admin-side-group-${group.questionnaireGroupId}`}
                      className="hover:bg-transparent! min-w-[8rem] flex-1"
                      active={false}
                      aria-pressed={active}
                      onClick={() => setSelected({ kind: "group", id: group.questionnaireGroupId })}
                      trailing={
                        <span className="flex items-center gap-1.5">
                          {String(group.status) === "DRAFT" ? (
                            <span className="rounded-full bg-ink/[0.06] px-1.5 py-0.5 text-smycles-micro text-ink">
                              {intl.formatMessage({ id: "admin.statusDraft", defaultMessage: "Draft" })}
                            </span>
                          ) : null}
                          {listsError ? null : (
                            <span className="text-smycles-label tabular-nums text-ink-muted">{members.length}</span>
                          )}
                        </span>
                      }
                    >
                      <span className="font-medium">{groupName(group.name)}</span>
                    </MenuItem>
                  </div>
                  {/* Same 0fr->1fr grid-rows trick as Disclosure (docs/UI_CONVENTIONS.md),
                      minus its settled/clip phase: that exists there to free
                      overflow for popovers inside the body once open, and this
                      body is plain text rows that never need to overflow it. */}
                  <div
                    inert={collapsed}
                    className={`grid transition-[grid-template-rows] duration-[var(--duration-base)] ${
                      collapsed ? "grid-rows-[0fr]" : "grid-rows-[1fr]"
                    }`}
                  >
                    <div className="overflow-hidden">
                      {listsError || visibility === "visible" ? null : (
                        <p className="smycles-field-meta px-2 pt-1 text-ink-muted">{notVisible}</p>
                      )}
                      <ul className="mt-1 space-y-1 border-l border-ink/10 pl-2">
                        {members.length === 0 ? (
                          <li className="px-1 py-1 text-smycles-label text-ink-muted">
                            {intl.formatMessage({
                              id: "admin.groupNoMembers",
                              defaultMessage: "No questionnaire assigned yet.",
                            })}
                          </li>
                        ) : (
                          members.map(questionnaireRow)
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}

            {ungrouped.length > 0 ? (
              <div>
                <div className="smycles-field-label px-2 text-ink-muted">
                  {intl.formatMessage({ id: "admin.ungrouped", defaultMessage: "Without group" })}
                </div>
                {listsError ? null : <p className="smycles-field-meta px-2 text-ink-muted">{notVisible}</p>}
                <ul className="mt-1 space-y-1">{ungrouped.map(questionnaireRow)}</ul>
              </div>
            ) : null}
          </div>
        </>
      }
    >
      {selectionGone ? (
        <div className="box flex min-h-[200px] items-center justify-center bg-surface p-6 text-smycles-meta text-ink-muted">
          {intl.formatMessage({
            id: "admin.selectionGone",
            defaultMessage: "This entry no longer exists. Pick another one on the left.",
          })}
        </div>
      ) : selected?.kind === "group" ? (
        <QuestionnaireGroupEditor
          key={selectionKey(selected)}
          group={selectedSection?.group ?? null}
          members={selectedSection?.members ?? []}
          allQuestionnaires={list}
          groups={groups}
          blockedReason={listsError ? userErrorMessage(intl, listsError) : null}
          onSaved={(id) => {
            flashSaved();
            selectAfterRefetch({ kind: "group", id });
          }}
          onDeleted={() => {
            void refetchAll();
            setSelected(null);
          }}
          onCancel={() => setSelected(null)}
          onChanged={() => void refetchAll()}
        />
      ) : selected?.kind === "questionnaire" ? (
        <Editor
          key={selectionKey(selected)}
          entry={selectedQuestionnaire}
          groups={groups}
          onSaved={(id) => {
            flashSaved();
            selectAfterRefetch({ kind: "questionnaire", id });
          }}
          onDeleted={() => {
            void refetchAll();
            setSelected(null);
          }}
          onCancel={() => setSelected(null)}
          onChanged={() => void refetchAll()}
        />
      ) : (
        <div id="admin-empty" className="box flex min-h-[200px] items-center justify-center bg-surface p-6 text-smycles-meta text-ink-muted">
          {intl.formatMessage({
            id: "admin.selectOrCreate",
            defaultMessage: "Select a group or questionnaire on the left, or create a new one.",
          })}
        </div>
      )}
    </AdminMasterDetail>
    {justSaved ? (
      <div id="admin-saved" className="fixed bottom-4 left-1/2 z-[var(--z-toast)] w-[28rem] max-w-[calc(100vw-2rem)] -translate-x-1/2 sm:bottom-8">
        <div className="smycles-toast">
          <span className="flex min-h-6 min-w-0 flex-1 items-center">
            {intl.formatMessage({ id: "admin.changesSaved", defaultMessage: "Changes saved." })}
          </span>
        </div>
      </div>
    ) : null}
    </>
  );
}

/**
 * `entry` is the row the list holds, or null while creating one. It carries the
 * id as well, so an existing questionnaire can never be edited without the row
 * that describes it: saveQuestionnaire upserts the whole graph, and a form
 * seeded from a missing row would write its blanks back over real data.
 */
function Editor({
  entry,
  groups,
  onSaved,
  onDeleted,
  onCancel,
  onChanged,
}: {
  entry: QuestionnaireRow | null;
  groups: GroupRow[];
  onSaved: (id: string) => void;
  onDeleted: () => void;
  onCancel: () => void;
  onChanged: () => void;
}) {
  const intl = useIntl();
  const { data, loading, error } = useQuery(QuestionnaireDetailDocument, {
    variables: { questionnaireId: entry?.questionnaireId ?? "" },
    skip: entry === null,
    fetchPolicy: "cache-and-network",
  });

  if (entry === null) {
    return (
      <EditorForm
        key="new"
        initial={{
          name: "",
          instructions: "",
          creativityElement: null,
          status: "ACTIVE",
          questions: [emptyQuestion("")],
        }}
        entry={null}
        groups={groups}
        onSaved={onSaved}
        onDeleted={onDeleted}
        onCancel={onCancel}
        onChanged={onChanged}
      />
    );
  }

  if (loading && !data) {
    return <div className="box bg-surface p-6 text-smycles-meta text-ink-muted">{intl.formatMessage({ id: "common.loading", defaultMessage: "Loading…" })}</div>;
  }

  // Never build a draft out of a failed load, for the same reason: a form that
  // silently shows zero questions would delete every real one on Save.
  if (error || !data) {
    return (
      <div className="box bg-surface p-6">
        <div className="text-smycles-meta text-red-600">{userErrorMessage(intl, error)}</div>
        <div className="mt-3">
          <Button variant="secondary" onClick={onCancel}>
            {intl.formatMessage({ id: "common.cancel", defaultMessage: "Cancel" })}
          </Button>
        </div>
      </div>
    );
  }

  const initial: Draft = {
    questionnaireId: entry.questionnaireId,
    name: entry.name,
    instructions: entry.instructions ?? "",
    creativityElement: entry.creativityElement ? String(entry.creativityElement) : null,
    status: String(entry.status) === "INACTIVE" ? "INACTIVE" : "ACTIVE",
    questions: [...data.questionnaireQuestions]
      .sort((a, b) => a.position - b.position)
      .map((q) => ({
        qnrQuestionId: q.qnrQuestionId,
        question: q.question,
        creativityElement: String(q.creativityElement),
        status: String(q.status),
        choices: [...q.answerChoices]
          .sort((a, b) => a.position - b.position)
          .map((c) => ({
            qnrAnswerChoiceId: c.qnrAnswerChoiceId,
            answer: c.answer,
            weight: c.weight,
          })),
      })),
  };

  return (
    <EditorForm
      key={entry.questionnaireId}
      initial={initial}
      entry={entry}
      groups={groups}
      onSaved={onSaved}
      onDeleted={onDeleted}
      onCancel={onCancel}
      onChanged={onChanged}
    />
  );
}

function EditorForm({
  initial,
  entry,
  groups,
  onSaved,
  onDeleted,
  onCancel,
  onChanged,
}: {
  initial: Draft;
  entry: QuestionnaireRow | null;
  groups: GroupRow[];
  onSaved: (id: string) => void;
  onDeleted: () => void;
  onCancel: () => void;
  onChanged: () => void;
}) {
  const intl = useIntl();
  const EL_OPTIONS = useElOptions();
  const [draft, setDraft] = useState<Draft>(initial);
  const STATUS_OPTIONS: FieldOption[] = STATUS_OPTION_IDS.map((o) => ({
    value: o.value,
    label: intl.formatMessage({ id: o.labelId }),
  }));
  const [save, { loading, error }] = useMutation(SaveQuestionnaireDocument);
  const [del, { loading: deleting, error: deleteError }] = useMutation(DeleteQuestionnaireDocument);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  // Narrowed once here so the delete/assignment branches below carry a real
  // string into their closures instead of re-checking a property.
  const savedId = draft.questionnaireId;

  async function remove(questionnaireId: string) {
    try {
      await del({ variables: { questionnaireId } });
      onDeleted();
    } catch {
      logApplicationFailure("admin.delete-questionnaire");
    }
  }

  const valid =
    draft.name.trim().length > 0 &&
    draft.questions.length > 0 &&
    draft.questions.every(
      (q) =>
        q.question.trim().length > 0 &&
        q.creativityElement &&
        q.choices.length > 0 &&
        q.choices.every((c) => c.answer.trim().length > 0),
    );

  function patch(p: Partial<Draft>) {
    setDraft((d) => ({ ...d, ...p }));
  }
  function patchQuestion(i: number, p: Partial<DraftQuestion>) {
    setDraft((d) => ({
      ...d,
      questions: d.questions.map((q, qi) => (qi === i ? { ...q, ...p } : q)),
    }));
  }

  async function submit() {
    try {
      const res = await save({
        variables: {
          input: {
            questionnaire: {
              questionnaireId: draft.questionnaireId,
              name: draft.name.trim(),
              status: draft.status as unknown as QuestionnaireStatus,
              creativityElement: draft.creativityElement
                ? (draft.creativityElement as unknown as CreativityElement)
                : null,
              instructions: draft.instructions.trim() || null,
            },
            questions: draft.questions.map((q, qi) => ({
              qnrQuestionId: q.qnrQuestionId,
              question: q.question.trim(),
              creativityElement: q.creativityElement as unknown as CreativityElement,
              position: qi,
              status: q.status as unknown as QuestionnaireStatus,
              answerChoices: q.choices.map((c, ci) => ({
                qnrAnswerChoiceId: c.qnrAnswerChoiceId,
                answer: c.answer.trim(),
                weight: c.weight,
                position: ci,
                status: "ACTIVE" as unknown as QuestionnaireStatus,
              })),
            })),
          },
        },
      });
      const id = res.data?.saveQuestionnaire.questionnaire.questionnaireId;
      if (id) onSaved(id);
    } catch {
      logApplicationFailure("admin.save-questionnaire");
    }
  }

  return (
    <div className="box bg-surface p-5 @sm:p-6">
      <div className="grid grid-cols-1 gap-4 @sm:grid-cols-[1fr_auto]">
        <label className="block">
          <span className="smycles-field-label text-ink-muted">{intl.formatMessage({ id: "admin.nameLabel", defaultMessage: "Name" })}</span>
          <input
            id="admin-questionnaire-name"
            className={`mt-1 ${inputCls}`}
            value={draft.name}
            onChange={(e) => patch({ name: e.target.value })}
            placeholder={intl.formatMessage({ id: "admin.namePlaceholder", defaultMessage: "e.g. Empathy check" })}
          />
        </label>
        <label className="block @sm:w-40">
          <span className="smycles-field-label text-ink-muted">{intl.formatMessage({ id: "admin.statusLabel", defaultMessage: "Status" })}</span>
          <Select
            id="admin-questionnaire-status"
            className="mt-1"
            value={draft.status}
            onChange={(v) => patch({ status: v as QStatus })}
            options={STATUS_OPTIONS}
          />
        </label>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 @sm:grid-cols-[1fr_auto]">
        <label className="block">
          <span className="smycles-field-label text-ink-muted">{intl.formatMessage({ id: "admin.instructionsLabel", defaultMessage: "Instructions (optional)" })}</span>
          <textarea
            className={`mt-1 ${inputCls} min-h-[60px] resize-y`}
            value={draft.instructions}
            onChange={(e) => patch({ instructions: e.target.value })}
            placeholder={intl.formatMessage({ id: "admin.instructionsPlaceholder", defaultMessage: "Hint for the person filling it out" })}
          />
        </label>
        <label className="block @sm:w-56">
          <span className="smycles-field-label text-ink-muted">{intl.formatMessage({ id: "admin.defaultPowerLabel", defaultMessage: "Default power (optional)" })}</span>
          <Select
            id="admin-questionnaire-defaultpower"
            className="mt-1"
            value={draft.creativityElement}
            onChange={(v) => patch({ creativityElement: v })}
            options={EL_OPTIONS}
            placeholder={intl.formatMessage({ id: "admin.nonePlaceholder", defaultMessage: "None" })}
          />
        </label>
      </div>

      {savedId ? (
        <GroupAssignment
          questionnaireId={savedId}
          questionnaireName={draft.name}
          currentGroupId={entry?.questionnaireGroupId ?? null}
          currentPosition={entry?.position ?? null}
          groups={groups}
          onChanged={onChanged}
        />
      ) : null}

      <div className="mt-6 space-y-4">
        {draft.questions.map((q, qi) => (
          <QuestionCard
            key={qi}
            index={qi}
            q={q}
            canRemove={draft.questions.length > 1}
            onChange={(p) => patchQuestion(qi, p)}
            onRemove={() =>
              patch({ questions: draft.questions.filter((_, x) => x !== qi) })
            }
          />
        ))}
        <PlusButton
          onClick={() =>
            patch({ questions: [...draft.questions, emptyQuestion(draft.creativityElement ?? "")] })
          }
          label={intl.formatMessage({ id: "admin.addQuestion", defaultMessage: "Add question" })}
        />
      </div>

      {error ? (
        <div className="mt-4 text-smycles-meta text-red-600">{userErrorMessage(intl, error)}</div>
      ) : null}
      {deleteError ? (
        <div className="mt-4 text-smycles-meta text-red-600">{userErrorMessage(intl, deleteError)}</div>
      ) : null}

      <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-ink/5 pt-4">
        <Button
          id="admin-questionnaire-save"
          variant="primary"
          onClick={() => void submit()}
          disabled={!valid || loading}
        >
          {loading
            ? intl.formatMessage({ id: "common.saving", defaultMessage: "Saving…" })
            : intl.formatMessage({ id: "common.save", defaultMessage: "Save" })}
        </Button>
        <Button id="admin-questionnaire-cancel" variant="secondary" onClick={onCancel}>
          {intl.formatMessage({ id: "common.cancel", defaultMessage: "Cancel" })}
        </Button>
        {!valid ? (
          <span className="text-smycles-label text-ink-muted">
            {intl.formatMessage({ id: "admin.validationHint", defaultMessage: "Name, plus text + power per question and text per answer required." })}
          </span>
        ) : null}
        {savedId ? (
          confirmingDelete ? (
            <div className="ml-auto flex items-center gap-2">
              <span className="text-smycles-label text-ink-muted">{intl.formatMessage({ id: "admin.confirmDeleteQ", defaultMessage: "Delete questionnaire?" })}</span>
              <Button
                id="admin-questionnaire-delete-confirm"
                variant="destructive"
                size="sm"
                onClick={() => void remove(savedId)}
                disabled={deleting}
              >
                {deleting ? "…" : intl.formatMessage({ id: "admin.yesDelete", defaultMessage: "Yes, delete" })}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setConfirmingDelete(false)}
                disabled={deleting}
              >
                {intl.formatMessage({ id: "common.cancel", defaultMessage: "Cancel" })}
              </Button>
            </div>
          ) : (
            <Button
              id="admin-questionnaire-delete"
              variant="ghost"
              size="sm"
              onClick={() => setConfirmingDelete(true)}
              className="ml-auto"
            >
              {intl.formatMessage({ id: "common.delete", defaultMessage: "Delete" })}
            </Button>
          )
        ) : null}
      </div>
    </div>
  );
}

/** A move the user picked but has not confirmed yet, carrying both ends by value. */
type PendingMove =
  | { kind: "move"; from: GroupRow | null; target: GroupRow }
  | { kind: "ungroup"; from: GroupRow };

/**
 * The consequence, spelled out. Three shapes, because "where do my answers go"
 * has three different answers: into another group's evaluation, into a group's
 * evaluation for the first time, or out of one.
 */
function moveMessage(intl: IntlShape, name: string, pending: PendingMove): string {
  if (pending.kind === "ungroup") {
    return intl.formatMessage(
      {
        id: "admin.ungroupMessage",
        defaultMessage:
          "“{name}” leaves “{from}” and loses its position. Its answers no longer count towards the evaluation of “{from}”.",
      },
      { name, from: groupName(pending.from.name) },
    );
  }
  if (pending.from) {
    return intl.formatMessage(
      {
        id: "admin.rehomeFromMessage",
        defaultMessage:
          "“{name}” moves out of “{from}” and into “{to}”, at the end of the order. Answers already given move with it and count towards the evaluation of “{to}” from now on.",
      },
      { name, from: groupName(pending.from.name), to: groupName(pending.target.name) },
    );
  }
  return intl.formatMessage(
    {
      id: "admin.rehomeMessage",
      defaultMessage:
        "“{name}” is placed at the end of “{to}”. Answers already given count towards the evaluation of “{to}” from now on.",
    },
    { name, to: groupName(pending.target.name) },
  );
}

/**
 * Where this questionnaire sits: which group it belongs to, and where inside
 * it. Deliberately apart from the draft above — genesis has no group field on
 * saveQuestionnaire, and moving a questionnaire moves the answers people have
 * already given into the target group's evaluation, which is not something a
 * Save button should do silently.
 */
function GroupAssignment({
  questionnaireId,
  questionnaireName,
  currentGroupId,
  currentPosition,
  groups,
  onChanged,
}: {
  questionnaireId: string;
  questionnaireName: string;
  currentGroupId: string | null;
  currentPosition: number | null;
  groups: GroupRow[];
  onChanged: () => void;
}) {
  const intl = useIntl();
  const [assign, { loading, error }] = useMutation(AssignQuestionnaireToGroupDocument);
  const [pending, setPending] = useState<PendingMove | null>(null);
  // null = follow the server. Only a number the user actually dialled in is
  // held here, so a position the server picked (re-homing appends at the end)
  // shows up straight away instead of leaving a stale value behind an Apply
  // button that would quietly move the questionnaire somewhere else.
  const [positionDraft, setPositionDraft] = useState<number | null>(null);

  const byId = new Map(groups.map((g) => [g.questionnaireGroupId, g] as const));
  const options: FieldOption[] = groups.map((g) => ({
    value: g.questionnaireGroupId,
    label: groupName(g.name),
  }));
  // A group the catalogue does not know reads as no group at all, the same way
  // buildGroupedList files such a questionnaire into the loose bucket. Better
  // than naming an empty group in a confirmation, and assigning it to a real
  // group is the repair either way.
  const currentGroup = (currentGroupId ? byId.get(currentGroupId) : undefined) ?? null;
  const groupId = currentGroup?.questionnaireGroupId ?? null;

  async function run(target: string | null, position: number | null) {
    try {
      await assign({
        variables: { input: { questionnaireId, questionnaireGroupId: target, position } },
      });
      setPositionDraft(null);
      onChanged();
    } catch {
      logApplicationFailure("admin.assign-questionnaire-to-group");
    }
  }

  const shownPosition = positionDraft ?? currentPosition ?? 0;
  const positionDirty =
    groupId !== null && positionDraft !== null && positionDraft !== currentPosition;

  return (
    <div className="bg-page box-flat mt-4 p-4">
      <div className="mb-1 text-smycles-body font-medium">
        {intl.formatMessage({ id: "admin.groupAssignmentTitle", defaultMessage: "Assignment" })}
      </div>
      <p className="smycles-field-meta mb-3 text-ink-muted">
        {intl.formatMessage({
          id: "admin.assignmentImmediateHint",
          defaultMessage: "Changes here take effect immediately, independently of Save.",
        })}
      </p>

      <div className="flex flex-wrap items-end gap-4">
        <label className="block min-w-[200px] flex-1">
          <span className="smycles-field-label text-ink-muted">
            {intl.formatMessage({ id: "admin.groupLabel", defaultMessage: "Group" })}
          </span>
          <Select
            className="mt-1"
            value={groupId}
            onChange={(v) => {
              const target = v ? byId.get(v) : undefined;
              // Re-picking what it already is would open a confirm for a no-op.
              if (target) {
                if (target.questionnaireGroupId !== groupId) {
                  setPending({ kind: "move", from: currentGroup, target });
                }
              } else if (currentGroup) {
                setPending({ kind: "ungroup", from: currentGroup });
              }
            }}
            options={options}
            disabled={loading}
            placeholder={intl.formatMessage({
              id: "admin.ungrouped",
              defaultMessage: "Without group",
            })}
          />
        </label>

        {groupId ? (
          <label className="block">
            <span className="smycles-field-label text-ink-muted">
              {intl.formatMessage({
                id: "admin.memberPositionLabel",
                defaultMessage: "Position in the group",
              })}
            </span>
            <div className="mt-1 flex items-center gap-2">
              <NumberStepper
                value={shownPosition}
                onChange={(next) => setPositionDraft(Math.round(next))}
                min={0}
                max={MAX_POSITION}
                disabled={loading}
                ariaLabel={intl.formatMessage({
                  id: "admin.memberPositionLabel",
                  defaultMessage: "Position in the group",
                })}
              />
              {positionDirty ? (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={loading}
                  onClick={() => void run(groupId, positionDraft)}
                >
                  {intl.formatMessage({ id: "admin.applyPosition", defaultMessage: "Apply" })}
                </Button>
              ) : null}
            </div>
          </label>
        ) : null}
      </div>

      {groupId === null ? (
        <p className="smycles-field-meta mt-2 text-ink-muted">
          {intl.formatMessage({
            id: "admin.notVisibleInGrow",
            defaultMessage: "Not in the Grow play catalogue.",
          })}
        </p>
      ) : null}

      {error ? (
        <div className="mt-2 text-smycles-meta text-red-600">{userErrorMessage(intl, error)}</div>
      ) : null}

      <ConfirmDialog
        open={pending !== null}
        destructive={pending?.kind === "ungroup"}
        // Clearing a group is a removal, not the ticket's "Umhängen": it needs
        // its own title and a neutral verb, not a red button that says "Move".
        title={
          pending?.kind === "ungroup"
            ? intl.formatMessage(
                { id: "admin.ungroupTitle", defaultMessage: "Remove “{name}” from its group?" },
                { name: questionnaireName },
              )
            : intl.formatMessage(
                { id: "admin.rehomeTitle", defaultMessage: "Move “{name}”?" },
                { name: questionnaireName },
              )
        }
        message={pending ? moveMessage(intl, questionnaireName, pending) : null}
        confirmLabel={
          pending?.kind === "ungroup"
            ? intl.formatMessage({ id: "common.remove", defaultMessage: "Remove" })
            : intl.formatMessage({ id: "admin.rehomeConfirm", defaultMessage: "Move" })
        }
        onConfirm={() => {
          if (!pending) return;
          const target = pending.kind === "ungroup" ? null : pending.target.questionnaireGroupId;
          setPending(null);
          void run(target, null);
        }}
        onCancel={() => setPending(null)}
      />
    </div>
  );
}

function QuestionCard({
  index,
  q,
  canRemove,
  onChange,
  onRemove,
}: {
  index: number;
  q: DraftQuestion;
  canRemove: boolean;
  onChange: (p: Partial<DraftQuestion>) => void;
  onRemove: () => void;
}) {
  const intl = useIntl();
  const EL_OPTIONS = useElOptions();
  function patchChoice(ci: number, p: Partial<DraftChoice>) {
    onChange({ choices: q.choices.map((c, x) => (x === ci ? { ...c, ...p } : c)) });
  }
  return (
    <div className="box-flat bg-page/40 p-4">
      <div className="mb-3 flex items-start gap-3">
        <span className="mt-2 shrink-0 text-smycles-label tabular-nums text-ink-muted">{index + 1}.</span>
        <input
          id={`admin-question-${index}`}
          className={inputCls}
          value={q.question}
          onChange={(e) => onChange({ question: e.target.value })}
          placeholder={intl.formatMessage({ id: "admin.questionPlaceholder", defaultMessage: "Question / statement" })}
        />
        <div className="w-44 shrink-0">
          <Select
            id={`admin-questionpower-${index}`}
            value={q.creativityElement}
            onChange={(v) => onChange({ creativityElement: v })}
            options={EL_OPTIONS}
            placeholder={intl.formatMessage({ id: "admin.selectPower", defaultMessage: "Select power" })}
          />
        </div>
        {canRemove ? (
          <Button
            variant="icon"
            size="sm"
            onClick={onRemove}
            aria-label={intl.formatMessage({ id: "admin.removeQuestion", defaultMessage: "Remove question" })}
            className="mt-1 shrink-0"
          >
            ✕
          </Button>
        ) : null}
      </div>

      <div className="space-y-1.5 pl-7">
        {q.choices.map((c, ci) => (
          <div key={ci} className="flex items-center gap-2">
            <input
              id={`admin-choice-${index}-${ci}`}
              className={`${inputCls} flex-1`}
              value={c.answer}
              onChange={(e) => patchChoice(ci, { answer: e.target.value })}
              placeholder={intl.formatMessage({ id: "admin.answerPlaceholder", defaultMessage: "Answer option" })}
            />
            <label className="flex shrink-0 items-center gap-1.5">
              <span className="smycles-field-label text-ink-muted">{intl.formatMessage({ id: "admin.weightLabel", defaultMessage: "Weight" })}</span>
              {/* NumberStepper clamps internally and tolerates an empty draft, which also fixes the
                  old `Number(x) || 0`: emptying the field snapped it to 0 mid-edit so the next
                  keystroke appended to a zero. */}
              <NumberStepper
                value={c.weight}
                onChange={(next) => patchChoice(ci, { weight: Math.round(next) })}
                min={0}
                max={MAX_WEIGHT}
                ariaLabel={intl.formatMessage({ id: "admin.weightLabel", defaultMessage: "Weight" })}
              />
            </label>
            {q.choices.length > 1 ? (
              <Button
                variant="icon"
                size="sm"
                onClick={() => onChange({ choices: q.choices.filter((_, x) => x !== ci) })}
                aria-label={intl.formatMessage({ id: "admin.removeAnswer", defaultMessage: "Remove answer" })}
                className="shrink-0"
              >
                ✕
              </Button>
            ) : null}
          </div>
        ))}
        <PlusButton
          onClick={() => onChange({ choices: [...q.choices, { answer: "", weight: 0 }] })}
          className="mt-1"
          label={intl.formatMessage({ id: "admin.addAnswer", defaultMessage: "Answer" })}
        />
      </div>
    </div>
  );
}
